import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Tooltip } from './Tooltip';

/**
 * @param {object} props - Props.
 * @param {string} [props.title] - A title for the tooltip button.
 * @param {JSX.Element} props.primaryTooltipContent - The contents of the
 *    tooltip.
 * @param {JSX.Element} [props.secondaryTooltipContent] - The contents of the
 *    secondary tooltip (if required).
 * @param {boolean} [props.externalTooltip=false] - True if the tooltip should be a
 *    floating element (useful when overflow is hidden on a parent element which
 *    means you can't see the tooltip), else false (default).
 * @param {boolean} [props.suppressFocusStyles=false] - True if the default focus
 *    styles are to be suppressed in favour of some other custom styles, else
 *    false (default).
 * @param {boolean} [props.showOnHover=true] - True if we want to show the tooltip on
 *    hover, otherwise false.
 * @param {'light'|'dark'} [props.theme='light'] - Theme.
 * @param {'above'|'below'} [props.direction='above'] - Direction.
 * @param {boolean} [props.disabled] - True if the button is disables, else
 *    false.
 * @param {boolean} [props.hideArrow=false] - True if the arrow should be hidden,
 *    else false.
 * @param {boolean} [props.noPadding=false] - True if we wish to remove the default
 *    tooltip padding, else false.
 * @param {Function} [props.onClick] - A click event handler.
 * @param {object} [props.classNames] - CSS class name(s).
 * @param {any} props.children - The contents of the tooltip.
 * @returns {JSX.Element} Component.
 */
export const TooltipButton = ( {
    title,
    primaryTooltipContent,
    secondaryTooltipContent,
    externalTooltip = false,
    suppressFocusStyles = false,
    showOnHover = true,
    hideArrow = false,
    noPadding = false,
    theme = 'light',
    direction = 'above',
    classNames = '',
    children,
    ...otherProps
} ) => {
    const [ isOpen, setIsOpen ] = useState( false );
    
    /** @type {React.MutableRefObject<HTMLDivElement|null>} */
    const wrapper = useRef();
    
    /** @type {React.MutableRefObject<HTMLDivElement|null>} */
    const externalTooltipWrapper = useRef();

    /**
     * 1. Show
     * @param {KeyboardEvent} event - Keyup event.
     */
    const keyupEventHandler = ( { key } ) => {
        if ( key !== 'Tab' ) { return; }

        setIsOpen(
            wrapper.current.contains(
                document.activeElement
            )
        );
    };

    useEffect( () => {
        window.addEventListener( 'keyup', keyupEventHandler );

        // Append an empty element to act as a wrapper in which the tooltip can
        // be rendered.
        if ( externalTooltip ) {
            externalTooltipWrapper.current = document.createElement( 'div' );
            document.body.appendChild( externalTooltipWrapper.current );
        }

        return () => {
            window.removeEventListener( 'keyup', keyupEventHandler );

            externalTooltipWrapper.current &&
                document.body.removeChild( externalTooltipWrapper.current );
        };
    }, [] );

    return (
        <div
            className={ classnames( `tooltip-button ${ classNames?.component ?? '' }`, {
                'tooltip-button--suppress-focus-styles': !!suppressFocusStyles
            } ) }
            ref={ wrapper }
            onMouseEnter={ () => showOnHover && setIsOpen( true ) }
            onMouseLeave={ () => setIsOpen( false ) }
        >
            <button
                {...otherProps}
                title={ title }
                className={ `tooltip-button__primary-tooltip-button ${ classNames?.button ?? '' }` }
                onClick={ evt => {
                    setIsOpen( true );
                    otherProps.onClick && otherProps.onClick( evt );
                } }
            >
                { children }
            </button>

            { isOpen && !otherProps.disabled && (
                // @ts-ignore
                <Tooltip
                    direction={ direction }
                    theme={ theme }
                    hideArrow={ hideArrow }
                    noPadding={ noPadding }
                    tooltipTarget={ wrapper.current }
                    externalTooltipWrapper={ externalTooltipWrapper?.current }
                    primaryTooltipContent={ primaryTooltipContent }
                    secondaryTooltipContent={ secondaryTooltipContent }
                />
            ) }
        </div>
    );
};

TooltipButton.propTypes = {
    primaryTooltipContent: PropTypes.any.isRequired,
    secondaryTooltipContent: PropTypes.any,
    children: PropTypes.any.isRequired,
    classNames: PropTypes.object,
    onClick: PropTypes.func,
    externalTooltip: PropTypes.bool,
    suppressFocusStyles: PropTypes.bool,
    showOnHover: PropTypes.bool,
    hideArrow: PropTypes.bool,
    noPadding: PropTypes.bool,
    theme: PropTypes.oneOf( [ 'light', 'dark' ] ),
    direction: PropTypes.oneOf( [ 'above', 'below' ] ),
    disabled: PropTypes.bool,
    title: PropTypes.string
};
